import { UserRole } from '../redux/authSlice';
import routePaths from '../global/routePath';

export type Permission = {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove?: boolean; // For Checker role
  canSync?: boolean; // For sync action
  canUpdateStatus?:boolean
};

export type ModulePermissions = {
  dashboard: Permission;
  institutions: Permission;
  screens: Permission;
  frames: Permission;
  alerts: Permission;
  playbackHistory: Permission;
  contentLibrary: Permission;
  users: Permission;
  componentContentHistory: Permission;
  screenContent: Permission; // Special for Maker/Checker
  screenFrameConfigs?: Permission;
  publishContent?:Permission;
  componentContent?: Permission;
  previewContent?: Permission;
};

const defaultPermission: Permission = {
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canApprove: false,
  canSync:false,
  canUpdateStatus: false
};

const fullPermission: Permission = {
  canView: true,
  canCreate: true,
  canEdit: true,
  canDelete: true,
  canApprove: true,
  canSync:true,
  canUpdateStatus: true
};

const viewOnlyPermission: Permission = {
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canApprove: false,
  canSync:false,
  canUpdateStatus: false
};

// Role-based permissions configuration
export const ROLE_PERMISSIONS: Record<UserRole, ModulePermissions> = {
  [UserRole.SuperAdmin]: {
    dashboard: fullPermission,
    institutions: fullPermission,
    screens: fullPermission,
    frames: fullPermission,
    alerts: fullPermission,
    playbackHistory: fullPermission,
    contentLibrary: fullPermission,
    users: fullPermission,
    screenContent: fullPermission,
    componentContentHistory: fullPermission,
    screenFrameConfigs: fullPermission,
    publishContent:fullPermission,
    componentContent: fullPermission,
    previewContent: fullPermission,
  },
  [UserRole.Admin]: {
    dashboard: viewOnlyPermission,
    institutions: viewOnlyPermission,
    screens: {
      canView: true,
      canSync: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    frames: viewOnlyPermission,
    alerts: fullPermission,
    playbackHistory: viewOnlyPermission,
    contentLibrary: fullPermission,
    users: fullPermission,
    screenContent: {
      canView: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canApprove: false,
      canUpdateStatus: true,
    },
    componentContentHistory: fullPermission,
    screenFrameConfigs: fullPermission,
    publishContent:viewOnlyPermission,
    componentContent: fullPermission,
    previewContent: fullPermission,
  },
  [UserRole.Maker]: {
    dashboard: viewOnlyPermission,
    institutions: defaultPermission,
    screens: {
      canView: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    frames: defaultPermission,
    alerts: defaultPermission,
    playbackHistory: defaultPermission,
    contentLibrary: defaultPermission,
    previewContent: defaultPermission,
    users: defaultPermission,
    screenContent: {
      canView: true,
      canCreate: true, // Can upload content
      canEdit: false,
      canDelete: false,
      canApprove: false,
    },
    componentContentHistory: viewOnlyPermission,
    screenFrameConfigs: viewOnlyPermission,
    publishContent: defaultPermission,
    componentContent: {
      canCreate: true,
      canView: true,
      canEdit: true,
      canDelete: false,
    },
  },
  [UserRole.Checker]: {
    dashboard: viewOnlyPermission,
    institutions: defaultPermission,
    screens: {
      canView: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    frames: defaultPermission,
    alerts: defaultPermission,
    playbackHistory: defaultPermission,
    contentLibrary: defaultPermission,
    users: defaultPermission,
    screenContent: {
      canView: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canApprove: true, // Can approve content
    },
    componentContentHistory: defaultPermission,
    screenFrameConfigs: viewOnlyPermission,
    publishContent: defaultPermission,
  },
  [UserRole.User]: {
    dashboard: viewOnlyPermission,
    institutions: defaultPermission,
    screens: defaultPermission,
    frames: defaultPermission,
    alerts: defaultPermission,
    playbackHistory: defaultPermission,
    contentLibrary: defaultPermission,
    users: defaultPermission,
    screenContent: defaultPermission,
    componentContentHistory: defaultPermission,
    screenFrameConfigs: viewOnlyPermission,
    previewContent: defaultPermission,
  },
  [UserRole.None]: {
    dashboard: defaultPermission,
    institutions: defaultPermission,
    screens: defaultPermission,
    frames: defaultPermission,
    alerts: defaultPermission,
    playbackHistory: defaultPermission,
    contentLibrary: defaultPermission,
    users: defaultPermission,
    screenContent: defaultPermission,
    componentContentHistory: defaultPermission,
    screenFrameConfigs: viewOnlyPermission,
  },
};

// Module to route path mapping - using centralized routePaths
export const MODULE_PATHS = {
  dashboard: [routePaths.home.base],
  institutions: [
    routePaths.institution.base,
    `${routePaths.institution.base}/*`
  ],
  screens: [
    routePaths.screen.base,
    `${routePaths.screen.base}/*`
  ],
  frames: [
    routePaths.frames.base,
    `${routePaths.frames.base}/*`
  ],
  alerts: [
    routePaths.alerts.base,
    `${routePaths.alerts.base}/*`
  ],
  playbackHistory: [
    routePaths.playbackHistory.base,
    `${routePaths.playbackHistory.base}/*`
  ],
  contentLibrary: [
    routePaths.contentLibrary.base,
    `${routePaths.contentLibrary.base}/*`
  ],
  users: [
    routePaths.users.base,
    `${routePaths.users.base}/*`
  ],
  screenContent: [
    routePaths.componentContent.base,
    `${routePaths.componentContent.base}/*`,
    '/screen-frame-config/*',
    '/screen-frame-configs/*'
  ],
  componentContentHistory: [
    routePaths.componentContentHistory.base,
    `${routePaths.componentContentHistory.base}/*`
  ],
  previewContent: [
    routePaths.preview.base,
    `${routePaths.preview.base}/*`
  ],
} as const;
